import { Symbols } from '@ioc/symbols'
import { inject, injectable } from 'inversify'

import { FetchClient } from '@writefull/core'

import { SessionManager } from '@infrastructure/managers/session/session-manager'

interface NLPResponse<T> {
  ok: boolean
  result: T
  error?: {
    code: string
    message: string
  }
  replacement_indices?: {
    start: number
    end: number
  }
}

@injectable()
export default class NLPBackendApi {
  constructor(
    @inject(Symbols.ENVIRONMENT) private readonly environment: ImportMetaEnv,
    @inject(FetchClient) private readonly fetchClient: FetchClient,
    @inject(SessionManager) private readonly sessionManager: SessionManager,
  ) {}

  public generateTitle(abstract: string): Promise<NLPResponse<string[]>> {
    return this.post<string[], NLPResponse<string[]>>(
      `${this.environment.VITE_WF_NLP_URL}/title-generator`,
      [abstract],
    )
  }

  public generateAbstract(text: string): Promise<NLPResponse<string>> {
    return this.post<string[], NLPResponse<string>>(
      `${this.environment.VITE_WF_NLP_URL}/abstract-generator`,
      [text],
    )
  }

  public academizer(text: string): Promise<NLPResponse<{ results: { rephrases: string[] }[] }>> {
    return this.post<string[], NLPResponse<{ results: { rephrases: string[] }[] }>>(
      `${this.environment.VITE_WF_NLP_URL}/academizer`,
      [text],
    )
  }

  public paraphrase(text: string, level: string): Promise<NLPResponse<{ paraphrases: string[] }>> {
    return this.post<{ text: string; level: string }[], NLPResponse<{ paraphrases: string[] }>>(
      `${this.environment.VITE_WF_NLP_URL}/paraphraser/multilevel`,
      [{ text, level }],
    )
  }

  private async post<P, T>(url: string, payload: P): Promise<T> {
    const token = await this.sessionManager.getIdToken()
    return this.fetchClient.post<T>(url, payload, undefined, {
      headers: token ? { 'firebase-token': token } : {},
    })
  }
}
